import type { MainColor } from '@awork/_shared/types/color'
import { Color } from '@awork/_shared/types/color'

const DERIVED_INITIALS_COLORS: MainColor[] = [Color.Red, Color.Blue, Color.Yellow, Color.Purple, Color.Green]

/**
 * Returns up to two initials for a display name.
 * @param {string} name Display name.
 * @returns {string} Uppercase initials.
 */
export function getAvatarInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('') || 'A'
  )
}

/**
 * Derives the web avatar color from its initials.
 * @param {string} initials Avatar initials.
 * @returns {MainColor} Shared avatar color.
 */
export function getAvatarInitialsColor(initials: string): MainColor {
  const letters = initials || ''
  const colors = DERIVED_INITIALS_COLORS

  // With 2 initials, there are 26*26 possible combinations
  // We got 5 colors.
  // We use the first letters index to decide on one of the colors.
  // The second letter shifts this for some more randomness
  const getIndexInAlphabet = (character: string): number => {
    if (!character) {
      return 0
    }

    const alphabet = 'abcdefghijklmnopqrstuvwxyz'
    const index = alphabet.indexOf(character.toLowerCase())

    return index >= 0 ? index : 0
  }

  const firstLetter = letters.length > 0 ? letters[0] : 'a'
  const secondLetter = letters.length > 1 ? letters[1] : 'a'
  let colorIndex = Math.floor((colors.length * getIndexInAlphabet(firstLetter)) / 26)
  const colorShift = Math.floor((colors.length * getIndexInAlphabet(secondLetter)) / 26)

  const shiftDown = colorIndex - colorShift
  const shiftUp = colorIndex + colorShift
  const blueIndex = 1

  if (shiftUp < colors.length - 1) {
    colorIndex = shiftUp
  } else if (shiftDown >= 0) {
    colorIndex = shiftDown
  } else {
    colorIndex = blueIndex
  }

  return colors[colorIndex] ?? Color.Blue
}
